import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { SERVICE_CATALOG } from "@/data/services";
import { CONTACT_INFO, whatsappHref } from "@/constants/contact";

export default function ServicesPage() {
  return (
    <div className="flex flex-col">
      {/* HERO */}
      <section className="relative overflow-hidden bg-brand-navy">
        {/* MOBILE (< lg): stacked — full image on top (no crop), text below */}
        <div className="lg:hidden">
          <div className="relative w-full aspect-[4/3] bg-brand-navy overflow-hidden">
            <img
              src="https://images.unsplash.com/photo-1758691736975-9f7f643d178e?auto=format&fit=crop&w=1600&q=85"
              alt="Diverse professionals in a modern Indian workplace"
              className="absolute inset-0 w-full h-full object-contain"
              style={{ objectPosition: "center center" }}
              loading="eager"
              draggable={false}
            />
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-b from-transparent to-brand-navy" />
          </div>
          <div className="relative bg-brand-navy px-5 py-10 sm:px-6 sm:py-12 overflow-hidden">
            <div className="absolute -top-8 -right-10 w-48 h-48 rounded-full bg-brand-teal/15 blur-3xl pointer-events-none" />
            <div className="absolute -bottom-8 -left-10 w-48 h-48 rounded-full bg-brand-royal/25 blur-3xl pointer-events-none" />
            <div className="relative z-10">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/15 border border-white/30 text-white text-[11px] font-semibold tracking-[0.22em] uppercase">
                <span className="h-1.5 w-1.5 rounded-full bg-brand-teal" /> Our Services
              </span>
              <h1 className="mt-5 font-heading font-extrabold text-white text-3xl sm:text-4xl leading-[1.08] tracking-tight">
                Recruitment Solutions for Every Stage of Hiring
              </h1>
              <p className="mt-4 text-sm sm:text-base text-white/95 leading-relaxed font-medium max-w-lg">
                From permanent hiring and executive search to bulk drives,
                contract staffing, campus hiring, HR consulting and payroll —
                one partner across the full workforce lifecycle.
              </p>
            </div>
          </div>
        </div>

        {/* DESKTOP (lg+): full-bleed image with strong overlay */}
        <div className="hidden lg:block relative">
          <div className="absolute inset-0">
            <img
              src="https://images.unsplash.com/photo-1758691736975-9f7f643d178e?auto=format&fit=crop&w=1920&q=80"
              alt="Diverse professionals in a modern Indian workplace"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-brand-navy/92 via-brand-navy/80 to-brand-royal/60" />
          </div>
          <div className="container-brand relative z-10 py-28 md:py-32">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/15 border border-white/30 text-white text-xs font-semibold tracking-[0.22em] uppercase">
              <span className="h-1.5 w-1.5 rounded-full bg-brand-teal" /> Our Services
            </span>
            <h1 className="mt-6 font-heading font-extrabold text-white text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-4xl">
              Recruitment Solutions for Every Stage of Hiring
            </h1>
            <p className="mt-6 max-w-2xl text-white/95 text-lg leading-relaxed font-medium">
              From permanent hiring and executive search to bulk drives, contract
              staffing, campus hiring, HR consulting and payroll — one partner
              across the full workforce lifecycle.
            </p>
          </div>
        </div>
      </section>

      {/* SERVICES GRID */}
      <section className="section-pad bg-white">
        <div className="container-brand">
          <div className="max-w-3xl">
            <span className="eyebrow">Recruitment Services</span>
            <h2 className="mt-3 heading-2">A complete recruitment &amp; staffing toolkit</h2>
            <p className="mt-4 body-lg">
              Click any service to see its dedicated page — with workflow,
              industries served, benefits, and a direct enquiry route.
            </p>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICE_CATALOG.map((s) => (
              <Link
                key={s.slug}
                to={`/services/${s.slug}`}
                className="group card-surface overflow-hidden hover:-translate-y-1 hover:border-brand-royal/40 transition-all"
              >
                <div className="relative h-44 overflow-hidden">
                  <img
                    src={s.image}
                    alt={s.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/60 via-brand-navy/10 to-transparent" />
                  <div className="absolute top-3 left-3 p-2 rounded-lg bg-white/95 backdrop-blur-sm">
                    <s.icon className="h-4 w-4 text-brand-royal" />
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="font-heading font-bold text-brand-navy text-lg">{s.title}</h3>
                  <p className="mt-2 text-sm text-brand-textSoft leading-relaxed line-clamp-3">
                    {s.tagline}
                  </p>
                  <div className="mt-4 text-sm font-semibold text-brand-royal flex items-center gap-1.5">
                    Learn More <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden bg-brand-royal">
        <div className="absolute inset-0 opacity-20">
          <img src="https://images.pexels.com/photos/7580648/pexels-photo-7580648.jpeg?auto=compress&cs=tinysrgb&w=1600" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-royal/95 to-brand-navy/95 mix-blend-multiply" />
        </div>
        <div className="container-brand section-pad relative z-10 text-center max-w-3xl">
          <span className="eyebrow-light">Get Started</span>
          <h2 className="mt-3 font-heading font-extrabold text-white text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight">
            Have a hiring requirement?
          </h2>
          <p className="mt-5 text-white/95 text-base sm:text-lg leading-relaxed font-medium">
            Share your workforce requirement with Ascend Tech Solutions and
            start a focused recruitment conversation.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
            <Link to="/contact?type=employer" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-white text-brand-royal font-semibold hover:bg-brand-soft transition-colors">
              Contact Our Team <ArrowRight className="h-4 w-4" />
            </Link>
            <a href={whatsappHref()} target="_blank" rel="noopener noreferrer" className="btn-ghost-light">
              <FaWhatsapp className="w-4 h-4" /> Chat on WhatsApp
            </a>
          </div>
          <p className="mt-6 text-white/80 text-xs">
            Or call us at{" "}
            <a href={CONTACT_INFO.callHrefTel} className="text-white font-semibold hover:text-brand-teal">
              {CONTACT_INFO.callNumberDisplay}
            </a>
          </p>
        </div>
      </section>
    </div>
  );
}
